import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import { DeleteModal } from "CommonComponent";
import isEqual from "lodash/isEqual";

const FULFILLMENT_SECTION_TITLE = "Fulfillment Letters and Postage";
const DEFAULT_PIECES_TITLE = "Cost of # Pieces ($)";
const DEFAULT_POSTAGE_TITLE = "Postage (at Cost)";

const CurrencyInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
  decimalScale = 2,
  prefix = "$ ",
  placeholder = "$ 0.00",
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => onChange?.({ target: { value: values.value } })}
    thousandSeparator
    decimalScale={decimalScale}
    allowNegative={false}
    prefix={prefix}
    placeholder={placeholder}
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
  />
);

const toBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const normalizeBrandName = (value) => String(value || "").trim();

const hasBrandMapValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return Number.isFinite(value);
  return String(value).trim() !== "";
};

const seedBrandMap = (existingMap, brands = [], fallbackValue = "") => {
  const next =
    existingMap && typeof existingMap === "object" && !Array.isArray(existingMap)
      ? { ...existingMap }
      : {};

  brands.forEach((brand) => {
    const brandName = normalizeBrandName(brand);
    if (!brandName) return;
    if (!hasBrandMapValue(next[brandName])) {
      next[brandName] = fallbackValue ?? "";
    }
  });

  return next;
};

const seedBrandwiseMaps = (
  config = {},
  brands = [],
  { piecesTitleFallback = DEFAULT_PIECES_TITLE, postageTitleFallback = DEFAULT_POSTAGE_TITLE } = {}
) => ({
  pieceTitleByBrand: seedBrandMap(
    config?.pieceTitleByBrand,
    brands,
    config?.title || piecesTitleFallback
  ),
  postageTitleByBrand: seedBrandMap(
    config?.postageTitleByBrand,
    brands,
    config?.postageTitle || postageTitleFallback
  ),
  perPieceRateByBrand: seedBrandMap(
    config?.perPieceRateByBrand,
    brands,
    config?.perPieceRate ?? ""
  ),
});

const dedupeBrands = (values = []) => {
  const seen = new Set();
  const unique = [];

  values.forEach((value) => {
    const normalized = normalizeBrandName(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(normalized);
  });

  return unique;
};

const extractBrandNamesFromRows = (rows = []) => {
  if (!Array.isArray(rows)) return [];

  return rows
    .map(
      (row) =>
        row?.brand_name ||
        row?.brandName ||
        row?.brand ||
        row?.program_brand ||
        row?.programBrand ||
        ""
    )
    .map(normalizeBrandName)
    .filter(Boolean);
};

const normalizeObjectMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((acc, [key, mapValue]) => {
    const normalizedKey = normalizeBrandName(key);
    if (!normalizedKey) return acc;
    acc[normalizedKey] = mapValue;
    return acc;
  }, {});
};

const normalizeStringArray = (value) =>
  Array.isArray(value) ? dedupeBrands(value) : [];

const normalizePostageRows = (value) =>
  Array.isArray(value) ? value.filter((row) => row && typeof row === "object") : [];

const createFulfillmentMiscRow = (id, data = {}) => ({
  id,
  title: data.title === undefined || data.title === null ? "" : String(data.title),
  rate: data.rate === undefined || data.rate === null ? "" : data.rate,
});

const toTwoDecimalString = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return "";
  const parsed = Number(String(value).replace(/[$,%\s,]/g, ""));
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
};

const normalizeFulfillmentMiscRows = (value) => {
  const source = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? [value]
      : [];

  return source
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createFulfillmentMiscRow(row?.id ?? index + 1, row));
};

const toFulfillmentMiscPayload = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      title: String(row?.title || "").trim(),
      rate: toTwoDecimalString(row?.rate),
    }))
    .filter((row) => row.title || row.rate);

const toRowArray = (value) =>
  Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? [value]
      : [];

const createTitleRow = (id, data = {}, defaultTitle = "") => ({
  id,
  title:
    data.title === undefined || data.title === null || String(data.title).trim() === ""
      ? defaultTitle
      : String(data.title),
});

const createGiftCardConfigRow = (id, data = {}) => ({
  id,
  title:
    data.title === undefined || data.title === null || String(data.title).trim() === ""
      ? "Gift Cards"
      : String(data.title),
  processingFee: data.processingFee === undefined || data.processingFee === null ? "" : data.processingFee,
});

const createFedexConfigRow = (id, data = {}) => ({
  id,
  title:
    data.title === undefined || data.title === null || String(data.title).trim() === ""
      ? "Shipment Charges"
      : String(data.title),
  baseCharge: data.baseCharge === undefined || data.baseCharge === null ? "" : data.baseCharge,
});

const normalizeTitleRows = (value, defaultTitle) =>
  toRowArray(value)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createTitleRow(row?.id ?? index + 1, row, defaultTitle));

const normalizeGiftCardConfigRows = (value) =>
  toRowArray(value)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createGiftCardConfigRow(row?.id ?? index + 1, row));

const normalizeFedexConfigRows = (value) =>
  toRowArray(value)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createFedexConfigRow(row?.id ?? index + 1, row));

const toTitleOnlyPayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    title: String(row?.title || "").trim(),
  }));

const toGiftCardsConfigPayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    title: String(row?.title || "").trim(),
    processingFee: toTwoDecimalString(row?.processingFee),
  }));

const toFedexConfigPayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    title: String(row?.title || "").trim(),
    baseCharge: toTwoDecimalString(row?.baseCharge),
  }));

const FulfilmentTab = ({
  selectedSections = [],
  onSectionDataChange,
  initialSectionData = {},
  getFieldError = () => "",
}) => {
  const shouldShow = (sectionId) => selectedSections.includes(sectionId);
  const hasSelectedSection = selectedSections.length > 0;

  const [fulfillmentConfig, setFulfillmentConfig] = useState({
    title: initialSectionData.fulfillment?.title || "Fulfillment",
    postageTitle:
      initialSectionData.fulfillment?.postageTitle || DEFAULT_POSTAGE_TITLE,
    perPieceRate: initialSectionData.fulfillment?.perPieceRate || "",
    chargeBrandwise: toBoolean(initialSectionData.fulfillment?.chargeBrandwise),
    program_brands: normalizeStringArray(initialSectionData.fulfillment?.program_brands),
    postage_rows: normalizePostageRows(
      initialSectionData.fulfillment?.postage_rows ||
        initialSectionData.fulfillment?.postage_data
    ),
    perPieceRateByBrand:
      normalizeObjectMap(initialSectionData.fulfillment?.perPieceRateByBrand),
    pieceTitleByBrand:
      normalizeObjectMap(initialSectionData.fulfillment?.pieceTitleByBrand),
    postageTitleByBrand:
      normalizeObjectMap(initialSectionData.fulfillment?.postageTitleByBrand),
  });

  const fulfillmentBrands = useMemo(
    () =>
      dedupeBrands([
        ...(Array.isArray(fulfillmentConfig?.program_brands)
          ? fulfillmentConfig.program_brands
          : []),
        ...(Array.isArray(initialSectionData?.fulfillment?.program_brands)
          ? initialSectionData.fulfillment.program_brands
          : []),
        ...extractBrandNamesFromRows(fulfillmentConfig?.postage_rows),
        ...extractBrandNamesFromRows(fulfillmentConfig?.postage_data),
        ...extractBrandNamesFromRows(initialSectionData?.fulfillment?.postage_rows),
        ...extractBrandNamesFromRows(initialSectionData?.fulfillment?.postage_data),
        ...Object.keys(fulfillmentConfig?.perPieceRateByBrand || {}),
        ...Object.keys(fulfillmentConfig?.pieceTitleByBrand || {}),
        ...Object.keys(fulfillmentConfig?.postageTitleByBrand || {}),
      ]),
    [
      fulfillmentConfig?.program_brands,
      initialSectionData?.fulfillment?.program_brands,
      fulfillmentConfig?.postage_rows,
      fulfillmentConfig?.postage_data,
      initialSectionData?.fulfillment?.postage_rows,
      initialSectionData?.fulfillment?.postage_data,
      fulfillmentConfig?.perPieceRateByBrand,
      fulfillmentConfig?.pieceTitleByBrand,
      fulfillmentConfig?.postageTitleByBrand,
    ]
  );

  const [trackerConfig, setTrackerConfig] = useState({
    checkProcessingTitle: "Check Processing",
    refundChecksIssuedTitle:
      initialSectionData.fulfillment_tracker?.refundChecksIssuedTitle ||
      "Refund Checks Issued",
    checkProcessingRate:
      initialSectionData.fulfillment_tracker?.checkProcessingRate || "",
    voidProcessingTitle: "Void",
    stoppedPaymentTitle: "Stopped Payment",
    ...(initialSectionData.fulfillment_tracker || {}),
  });

  const [tracker1Config, setTracker1Config] = useState({
    retrievalTitle:
      initialSectionData.fulfillment_tracker_1?.retrievalTitle || "Retrieval",
    retrievalRate:
      initialSectionData.fulfillment_tracker_1?.retrievalRate ||
      initialSectionData.fulfillment_tracker_1?.retrievalHoursRate ||
      "",
    nonRetrievalTitle:
      initialSectionData.fulfillment_tracker_1?.nonRetrievalTitle ||
      "Non-Retrieval",
    nonRetrievalRate:
      initialSectionData.fulfillment_tracker_1?.nonRetrievalRate ||
      "",
    billRetrievalByHours:
      toBoolean(initialSectionData.fulfillment_tracker_1?.billRetrievalByHours),
    minutesPerRetrieval:
      initialSectionData.fulfillment_tracker_1?.minutesPerRetrieval || "",
  });

  const [suppliesRows, setSuppliesRows] = useState(() =>
    normalizeTitleRows(initialSectionData.supplies, "Supplies")
  );
  const nextSuppliesIdRef = useRef(
    normalizeTitleRows(initialSectionData.supplies, "Supplies").length + 1
  );

  const [storageRows, setStorageRows] = useState(() =>
    normalizeTitleRows(initialSectionData.storage, "Storage")
  );
  const nextStorageIdRef = useRef(
    normalizeTitleRows(initialSectionData.storage, "Storage").length + 1
  );

  const [giftCardRows, setGiftCardRows] = useState(() =>
    normalizeGiftCardConfigRows(initialSectionData.gift_cards)
  );
  const nextGiftCardIdRef = useRef(
    normalizeGiftCardConfigRows(initialSectionData.gift_cards).length + 1
  );

  const [fedexRows, setFedexRows] = useState(() =>
    normalizeFedexConfigRows(initialSectionData.fedex_expenses)
  );
  const nextFedexIdRef = useRef(
    normalizeFedexConfigRows(initialSectionData.fedex_expenses).length + 1
  );

  const [fulfillmentMiscRows, setFulfillmentMiscRows] = useState(() =>
    normalizeFulfillmentMiscRows(initialSectionData.fulfillment_misc)
  );
  const nextFulfillmentMiscIdRef = useRef(
    normalizeFulfillmentMiscRows(initialSectionData.fulfillment_misc).length + 1
  );

  const lastEmittedPayloadRef = useRef(null);
  const onSectionDataChangeRef = useRef(onSectionDataChange);

  useEffect(() => {
    onSectionDataChangeRef.current = onSectionDataChange;
  }, [onSectionDataChange]);

  useEffect(() => {
    if (!toBoolean(fulfillmentConfig?.chargeBrandwise) || fulfillmentBrands.length === 0) {
      return;
    }

    setFulfillmentConfig((prev) => {
      const seeded = seedBrandwiseMaps(prev, fulfillmentBrands);
      if (
        isEqual(prev?.pieceTitleByBrand, seeded.pieceTitleByBrand) &&
        isEqual(prev?.postageTitleByBrand, seeded.postageTitleByBrand) &&
        isEqual(prev?.perPieceRateByBrand, seeded.perPieceRateByBrand)
      ) {
        return prev;
      }

      return {
        ...prev,
        ...seeded,
      };
    });
  }, [fulfillmentConfig?.chargeBrandwise, fulfillmentBrands]);

  useEffect(() => {
    const isChargeBrandwise = toBoolean(fulfillmentConfig?.chargeBrandwise);
    const normalizedProgramBrands = dedupeBrands([
      ...(Array.isArray(fulfillmentConfig?.program_brands)
        ? fulfillmentConfig.program_brands
        : []),
      ...extractBrandNamesFromRows(fulfillmentConfig?.postage_rows),
      ...extractBrandNamesFromRows(fulfillmentConfig?.postage_data),
      ...fulfillmentBrands,
    ]);

    const normalizedPostageRows = normalizePostageRows(
      fulfillmentConfig?.postage_rows || fulfillmentConfig?.postage_data
    );

    const seededBrandMaps = isChargeBrandwise
      ? seedBrandwiseMaps(
          {
            ...fulfillmentConfig,
            perPieceRateByBrand: normalizeObjectMap(fulfillmentConfig?.perPieceRateByBrand),
            pieceTitleByBrand: normalizeObjectMap(fulfillmentConfig?.pieceTitleByBrand),
            postageTitleByBrand: normalizeObjectMap(fulfillmentConfig?.postageTitleByBrand),
          },
          normalizedProgramBrands
        )
      : {
          perPieceRateByBrand: normalizeObjectMap(fulfillmentConfig?.perPieceRateByBrand),
          pieceTitleByBrand: normalizeObjectMap(fulfillmentConfig?.pieceTitleByBrand),
          postageTitleByBrand: normalizeObjectMap(fulfillmentConfig?.postageTitleByBrand),
        };

    const filteredFulfillmentConfig = {
      ...fulfillmentConfig,
      program_brands: normalizedProgramBrands,
      ...(normalizedPostageRows.length > 0
        ? { postage_rows: normalizedPostageRows }
        : {}),
      perPieceRateByBrand: seededBrandMaps.perPieceRateByBrand,
      pieceTitleByBrand: seededBrandMaps.pieceTitleByBrand,
      postageTitleByBrand: seededBrandMaps.postageTitleByBrand,
    };

    if (!isChargeBrandwise) {
      delete filteredFulfillmentConfig.chargeBrandwise;
      delete filteredFulfillmentConfig.perPieceRateByBrand;
      delete filteredFulfillmentConfig.pieceTitleByBrand;
      delete filteredFulfillmentConfig.postageTitleByBrand;
    } else {
      filteredFulfillmentConfig.chargeBrandwise = true;
    }

    const filteredTrackerConfig = { ...trackerConfig };
    const filteredTracker1Config = { ...tracker1Config };

    const unsupportedTrackerFields = ["voidProcessingRate", "stoppedPaymentRate"];
    unsupportedTrackerFields.forEach(field => delete filteredTrackerConfig[field]);

    if (!toBoolean(filteredTracker1Config.billRetrievalByHours)) {
      delete filteredTracker1Config.minutesPerRetrieval;
    }

    delete filteredTracker1Config.retrievalHoursRate;

    const nextPayload = {
      fulfillment: filteredFulfillmentConfig,
      fulfillment_tracker: filteredTrackerConfig,
      fulfillment_tracker_1: filteredTracker1Config,
      supplies: toTitleOnlyPayload(suppliesRows),
      storage: toTitleOnlyPayload(storageRows),
      gift_cards: toGiftCardsConfigPayload(giftCardRows),
      fedex_expenses: toFedexConfigPayload(fedexRows),
      fulfillment_misc: toFulfillmentMiscPayload(fulfillmentMiscRows),
    };

    if (isEqual(lastEmittedPayloadRef.current, nextPayload)) {
      return;
    }

    lastEmittedPayloadRef.current = nextPayload;
    onSectionDataChangeRef.current?.(nextPayload);
  }, [
    fulfillmentConfig,
    fulfillmentBrands,
    trackerConfig,
    tracker1Config,
    suppliesRows,
    storageRows,
    giftCardRows,
    fedexRows,
    fulfillmentMiscRows,
  ]);

  const handleChange = (setter, field) => (event) => {
    setter((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCheckboxChange = (setter, field) => (event) => {
    const checked = event.target.checked;
    setter((prev) => ({
      ...prev,
      [field]: checked,
      ...(field === "billRetrievalByHours" && !checked
        ? { minutesPerRetrieval: "" }
        : {}),
    }));
  };

  const getError = (sectionId, field, rowIndex = null) =>
    getFieldError?.(sectionId, field, rowIndex) || "";
  const getBrandMapError = (field, brand) => {
    const brandName = normalizeBrandName(brand);
    if (!brandName) return "";
    return (
      getError("fulfillment", `${field}.${brandName}`) ||
      getError("fulfillment", `${field}.${brand}`) ||
      getError("fulfillment", field)
    );
  };

  const handleChargeBrandwiseChange = (event) => {
    const checked = Boolean(event?.target?.checked);
    setFulfillmentConfig((prev) => {
      if (!checked) {
        return {
          ...prev,
          chargeBrandwise: false,
        };
      }

      return {
        ...prev,
        chargeBrandwise: true,
        ...seedBrandwiseMaps(prev, fulfillmentBrands),
      };
    });
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleRowFieldChange = (setter, rowId, field) => (event) => {
    const nextValue = event?.target?.value;
    setter((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: nextValue } : row))
    );
  };

  const handleFulfillmentMiscFieldChange = handleRowFieldChange.bind(
    null,
    setFulfillmentMiscRows
  );

  const handleAddFulfillmentMiscRow = () => {
    const nextId = nextFulfillmentMiscIdRef.current;
    nextFulfillmentMiscIdRef.current += 1;
    setFulfillmentMiscRows((prev) => [...prev, createFulfillmentMiscRow(nextId)]);
  };

  const handleAddTitleRow = (setter, idRef, createRow, defaultTitle) => () => {
    const nextId = idRef.current;
    idRef.current += 1;
    setter((prev) => [...prev, createRow(nextId, {}, defaultTitle)]);
  };

  const handleDeleteRowClick = (sectionId, rowId) => {
    setPendingDelete({ sectionId, rowId });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  const handleConfirmDeleteRow = () => {
    const sectionId = pendingDelete?.sectionId;
    const rowId = pendingDelete?.rowId;
    const setters = {
      fulfillment_misc: setFulfillmentMiscRows,
      supplies: setSuppliesRows,
      storage: setStorageRows,
      gift_cards: setGiftCardRows,
      fedex_expenses: setFedexRows,
    };
    setters[sectionId]?.((prev) => prev.filter((row) => row.id !== rowId));
    handleCloseDeleteModal();
  };

  return (
    <>
      {!hasSelectedSection && (
        <div className="invoice-section-div mb-0 text-center">
          <span className="font-16 font-weight-bold heading-color">
            First you have to select at least one section.
          </span>
        </div>
      )}

      {shouldShow("fulfillment") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">{FULFILLMENT_SECTION_TITLE}</span>
          </h3>
          <Row>
            <Col>
              <Form.Group className="mb-3 mt-2">
                <Form.Check
                  type="checkbox"
                  label="Charge Brandwise"
                  checked={toBoolean(fulfillmentConfig.chargeBrandwise)}
                  onChange={handleChargeBrandwiseChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="labor-hours-table-wrapper mb-0">
            <div className="labor-hours-table-inner" style={{ minWidth: "920px" }}>
              <div
                className="labor-hours-grid labor-hours-header-row"
                style={{ gridTemplateColumns: "1.2fr 1.9fr 1.3fr 1.9fr" }}
              >
                <div className="labor-hours-header-cell">Brand</div>
                <div className="labor-hours-header-cell">Pieces Title</div>
                <div className="labor-hours-header-cell">Per Piece Rate ($)</div>
                <div className="labor-hours-header-cell">Postage Title</div>
              </div>

              {fulfillmentBrands.length > 0 ? (
                fulfillmentBrands.map((brand) => {
                  const piecesTitle =
                    fulfillmentConfig?.pieceTitleByBrand?.[brand] ||
                    fulfillmentConfig?.title ||
                    DEFAULT_PIECES_TITLE;
                  const postageTitle =
                    fulfillmentConfig?.postageTitleByBrand?.[brand] ||
                    fulfillmentConfig?.postageTitle ||
                    DEFAULT_POSTAGE_TITLE;
                  const brandRate =
                    fulfillmentConfig?.perPieceRateByBrand?.[brand] ??
                    fulfillmentConfig?.perPieceRate ??
                    "";
                  const piecesTitleError = getBrandMapError("pieceTitleByBrand", brand);
                  const perPieceRateError = getBrandMapError("perPieceRateByBrand", brand);
                  const postageTitleError = getBrandMapError("postageTitleByBrand", brand);
                  const isChargeBrandwise = toBoolean(fulfillmentConfig.chargeBrandwise);

                  return (
                    <div
                      key={`config-brand-${brand}`}
                      className="labor-hours-grid labor-hours-data-row"
                      style={{ gridTemplateColumns: "1.2fr 1.9fr 1.3fr 1.9fr" }}
                    >
                      <Form.Control
                        type="text"
                        value={brand}
                        readOnly
                        className="labor-hours-input labor-hours-input-muted"
                      />

                      {isChargeBrandwise ? (
                        <>
                          <div>
                            <Form.Control
                              type="text"
                              value={piecesTitle}
                              onChange={(event) => {
                                const nextValue = event?.target?.value;
                                setFulfillmentConfig((prev) => ({
                                  ...prev,
                                  pieceTitleByBrand: {
                                    ...(prev?.pieceTitleByBrand || {}),
                                    [brand]: nextValue,
                                  },
                                }));
                              }}
                              isInvalid={Boolean(piecesTitleError)}
                              className="input-field"
                            />
                            <Form.Control.Feedback type="invalid">
                              {piecesTitleError}
                            </Form.Control.Feedback>
                          </div>

                          <div>
                            <CurrencyInput
                              value={brandRate}
                              onChange={(event) => {
                                const nextValue = event?.target?.value;
                                setFulfillmentConfig((prev) => ({
                                  ...prev,
                                  perPieceRateByBrand: {
                                    ...(prev?.perPieceRateByBrand || {}),
                                    [brand]: nextValue,
                                  },
                                }));
                              }}
                              isInvalid={Boolean(perPieceRateError)}
                              className="labor-hours-input"
                            />
                            <Form.Control.Feedback type="invalid">
                              {perPieceRateError}
                            </Form.Control.Feedback>
                          </div>

                          <div>
                            <Form.Control
                              type="text"
                              value={postageTitle}
                              onChange={(event) => {
                                const nextValue = event?.target?.value;
                                setFulfillmentConfig((prev) => ({
                                  ...prev,
                                  postageTitleByBrand: {
                                    ...(prev?.postageTitleByBrand || {}),
                                    [brand]: nextValue,
                                  },
                                }));
                              }}
                              isInvalid={Boolean(postageTitleError)}
                              className="input-field"
                            />
                            <Form.Control.Feedback type="invalid">
                              {postageTitleError}
                            </Form.Control.Feedback>
                          </div>
                        </>
                      ) : (
                        <>
                          <Form.Control
                            type="text"
                            value={piecesTitle}
                            readOnly
                            className="labor-hours-input labor-hours-input-muted"
                          />
                          <Form.Control
                            type="text"
                            value={
                              brandRate === undefined || brandRate === null || brandRate === ""
                                ? ""
                                : `$ ${brandRate}`
                            }
                            readOnly
                            className="labor-hours-input labor-hours-input-muted"
                          />
                          <Form.Control
                            type="text"
                            value={postageTitle}
                            readOnly
                            className="labor-hours-input labor-hours-input-muted"
                          />
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-muted py-3">No brand-wise data available.</div>
              )}

              <div
                className="labor-hours-grid labor-hours-data-row"
                style={{ gridTemplateColumns: "1.2fr 1.9fr 1.3fr 1.9fr", fontWeight: 600 }}
              >
                <div className="d-flex align-items-center">
                  {toBoolean(fulfillmentConfig.chargeBrandwise) ? "Program Total" : "Total"}
                </div>
                {toBoolean(fulfillmentConfig.chargeBrandwise) ? (
                  <>
                    <Form.Control
                      type="text"
                      value={fulfillmentConfig.title || DEFAULT_PIECES_TITLE}
                      readOnly
                      className="labor-hours-input labor-hours-input-muted"
                    />
                    <Form.Control
                      type="text"
                      value={
                        fulfillmentConfig.perPieceRate === undefined ||
                        fulfillmentConfig.perPieceRate === null ||
                        fulfillmentConfig.perPieceRate === ""
                          ? ""
                          : `$ ${fulfillmentConfig.perPieceRate}`
                      }
                      readOnly
                      className="labor-hours-input labor-hours-input-muted"
                    />
                    <Form.Control
                      type="text"
                      value={fulfillmentConfig.postageTitle || DEFAULT_POSTAGE_TITLE}
                      readOnly
                      className="labor-hours-input labor-hours-input-muted"
                    />
                  </>
                ) : (
                  <>
                    <div>
                      <Form.Control
                        type="text"
                        value={fulfillmentConfig.title || ""}
                        onChange={handleChange(setFulfillmentConfig, "title")}
                        isInvalid={Boolean(getError("fulfillment", "title"))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError("fulfillment", "title")}
                      </Form.Control.Feedback>
                    </div>
                    <div>
                      <CurrencyInput
                        value={fulfillmentConfig.perPieceRate}
                        onChange={handleChange(setFulfillmentConfig, "perPieceRate")}
                        isInvalid={Boolean(getError("fulfillment", "perPieceRate"))}
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError("fulfillment", "perPieceRate")}
                      </Form.Control.Feedback>
                    </div>
                    <div>
                      <Form.Control
                        type="text"
                        value={fulfillmentConfig.postageTitle || ""}
                        onChange={handleChange(setFulfillmentConfig, "postageTitle")}
                        isInvalid={Boolean(getError("fulfillment", "postageTitle"))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError("fulfillment", "postageTitle")}
                      </Form.Control.Feedback>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {shouldShow("fulfillment_misc") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Fulfilment Misc</span>
          </h3>
          {fulfillmentMiscRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleFulfillmentMiscFieldChange(row.id, "title")}
                      isInvalid={Boolean(getError("fulfillment_misc", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fulfillment_misc", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Rate ($)</Form.Label>
                    <CurrencyInput
                      value={row.rate}
                      onChange={handleFulfillmentMiscFieldChange(row.id, "rate")}
                      isInvalid={Boolean(getError("fulfillment_misc", "rate", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fulfillment_misc", "rate", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("fulfillment_misc", row.id)}
                    aria-label="Delete fulfilment misc row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddFulfillmentMiscRow}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("fulfillment_tracker") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">
              Refund checks
            </span>
          </h3>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Check Processing Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.checkProcessingTitle || ""}
                  onChange={handleChange(setTrackerConfig, "checkProcessingTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "checkProcessingTitle"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker", "checkProcessingTitle")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Refund Checks Issued Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.refundChecksIssuedTitle || ""}
                  onChange={handleChange(setTrackerConfig, "refundChecksIssuedTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "refundChecksIssuedTitle"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker", "refundChecksIssuedTitle")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Check Processing Rate ($)</Form.Label>
                <CurrencyInput
                  value={trackerConfig.checkProcessingRate}
                  onChange={handleChange(setTrackerConfig, "checkProcessingRate")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "checkProcessingRate"))}
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker", "checkProcessingRate")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Void Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.voidProcessingTitle || ""}
                  onChange={handleChange(setTrackerConfig, "voidProcessingTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "voidProcessingTitle"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker", "voidProcessingTitle")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Stopped Payment Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.stoppedPaymentTitle || ""}
                  onChange={handleChange(setTrackerConfig, "stoppedPaymentTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "stoppedPaymentTitle"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker", "stoppedPaymentTitle")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {shouldShow("fulfillment_tracker_1") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">
              RK/non-RK tracker
            </span>
          </h3>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Retrieval Title</Form.Label>
                <Form.Control
                  type="text"
                  value={tracker1Config.retrievalTitle || ""}
                  onChange={handleChange(setTracker1Config, "retrievalTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "retrievalTitle"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker_1", "retrievalTitle")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Non-Retrieval Title</Form.Label>
                <Form.Control
                  type="text"
                  value={tracker1Config.nonRetrievalTitle || ""}
                  onChange={handleChange(setTracker1Config, "nonRetrievalTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "nonRetrievalTitle"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker_1", "nonRetrievalTitle")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Retrieval Rate ($)</Form.Label>
                <CurrencyInput
                  value={tracker1Config.retrievalRate}
                  onChange={handleChange(setTracker1Config, "retrievalRate")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "retrievalRate"))}
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker_1", "retrievalRate")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Non-Retrieval Rate ($)</Form.Label>
                <CurrencyInput
                  value={tracker1Config.nonRetrievalRate}
                  onChange={handleChange(setTracker1Config, "nonRetrievalRate")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "nonRetrievalRate"))}
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker_1", "nonRetrievalRate")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3 d-flex align-items-center mt-4">
                <Form.Check
                  type="checkbox"
                  label="Bill Retrieval by Hours"
                  checked={toBoolean(tracker1Config.billRetrievalByHours)}
                  onChange={handleCheckboxChange(setTracker1Config, "billRetrievalByHours")}
                />
              </Form.Group>
            </Col>
          </Row>
          {toBoolean(tracker1Config.billRetrievalByHours) && (
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Minutes Per Retrieval</Form.Label>
                  <Form.Control
                    type="text"
                    value={tracker1Config.minutesPerRetrieval}
                    onChange={handleChange(setTracker1Config, "minutesPerRetrieval")}
                    isInvalid={Boolean(getError("fulfillment_tracker_1", "minutesPerRetrieval"))}
                    className="input-field"
                  />
                  <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker_1", "minutesPerRetrieval")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          )}
        </div>
      )}

      {shouldShow("supplies") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Supplies</span>
          </h3>
          {suppliesRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setSuppliesRows, row.id, "title")}
                      isInvalid={Boolean(getError("supplies", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("supplies", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("supplies", row.id)}
                    aria-label="Delete supplies row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddTitleRow(setSuppliesRows, nextSuppliesIdRef, createTitleRow, "Supplies")}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("storage") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Storage</span>
          </h3>
          {storageRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setStorageRows, row.id, "title")}
                      isInvalid={Boolean(getError("storage", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("storage", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("storage", row.id)}
                    aria-label="Delete storage row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddTitleRow(setStorageRows, nextStorageIdRef, createTitleRow, "Storage")}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("gift_cards") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Gift Cards</span>
          </h3>
          {giftCardRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setGiftCardRows, row.id, "title")}
                      isInvalid={Boolean(getError("gift_cards", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("gift_cards", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Processing Fee ($)</Form.Label>
                    <CurrencyInput
                      value={row.processingFee}
                      onChange={handleRowFieldChange(setGiftCardRows, row.id, "processingFee")}
                      isInvalid={Boolean(getError("gift_cards", "processingFee", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("gift_cards", "processingFee", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("gift_cards", row.id)}
                    aria-label="Delete gift cards row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddTitleRow(setGiftCardRows, nextGiftCardIdRef, createGiftCardConfigRow)}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("fedex_expenses") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Shipment Charges</span>
          </h3>
          {fedexRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setFedexRows, row.id, "title")}
                      isInvalid={Boolean(getError("fedex_expenses", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fedex_expenses", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Base Charge ($)</Form.Label>
                    <CurrencyInput
                      value={row.baseCharge}
                      onChange={handleRowFieldChange(setFedexRows, row.id, "baseCharge")}
                      isInvalid={Boolean(getError("fedex_expenses", "baseCharge", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fedex_expenses", "baseCharge", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("fedex_expenses", row.id)}
                    aria-label="Delete shipment charges row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddTitleRow(setFedexRows, nextFedexIdRef, createFedexConfigRow)}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onDelete={handleConfirmDeleteRow}
          message="Are you sure you want to delete this row?"
        />
      )}
    </>
  );
};

export default FulfilmentTab;